/**
 * CardOverviewHorizontal Component
 *
 * Renders a horizontal card layout for credit card offerings.
 * This variation displays the card image on the left with content on the right,
 * optimized for wider viewports and detailed information display.
 *
 * @module molecules/CardOverviewHorizontal
 */

// External library imports
import React, { FC, useRef, useMemo, memo } from 'react';
import clsx from 'clsx';

// Internal library imports
import { Button, RichText } from '@marriott/mi-ui-library';
import { CardOverviewHorizontalProps } from './CardOverviewHorizontal.types';
import { StyledCardOverviewHorizontal } from './CardOverviewHorizontal.styles';
import { CobrandImage } from '../CobrandImage/CobrandImage';
import { LinkType, LinkTarget, ARIA_LABELS, StringBoolean } from '../../modules/constants/constants';

// AEM component configuration
export const CardOverviewHorizontalConfig = {
  emptyLabel: 'CardOverviewHorizontal',
  isEmpty: () => true,
  resourceType: `/components/content/CardOverviewHorizontal`,
};

/**
 * CardOverviewHorizontal Component
 *
 * Performance optimizations:
 * - Memoized component with React.memo
 * - useMemo for computed values
 * - Uses constants for link types and targets
 *
 * Displays a credit card offer in horizontal layout with:
 * - Card image on the left side
 * - Header, points, and card details on the right side
 * - Optional separator lines between sections
 * - Call-to-action button
 *
 * @param props - Component properties
 * @returns JSX element representing the horizontal card layout
 */
export const CardOverviewHorizontal: FC<CardOverviewHorizontalProps> = memo<CardOverviewHorizontalProps>(props => {
  // Destructure all props for easier access
  const {
    cardType,
    imagePath,
    altText,
    header,
    pointSectionSeparator,
    pointSectionDescription,
    cardSectionSeparator,
    cardSectionDescription,
    linkText,
    linkUrl,
    openInaNewTab,
    componentId,
    customClass,
    styleclass,
    trackingProperties,
  } = props;

  // Refs for DOM access
  const buttonRef = useRef<HTMLDivElement>(null);

  // Memoized values for performance optimization
  const isOpenInNewTab = openInaNewTab === true || openInaNewTab === StringBoolean.TRUE;
  const linkType = useMemo<LinkType>(() => (isOpenInNewTab ? LinkType.EXTERNAL : LinkType.INTERNAL), [isOpenInNewTab]);

  // Render horizontal card layout
  return (
    <StyledCardOverviewHorizontal
      data-component-name="m-cobrand-CardOverviewHorizontal"
      data-testid="cobrand-CardOverviewHorizontal"
      id={componentId}
      className={clsx('card-overview d-flex justify-content-center flex-grow-1', styleclass)}
      data-cardtype={cardType}
    >
      <div
        className={clsx(
          'card-overview-bordered t-background-color',
          'd-flex flex-column flex-lg-row justify-content-between flex-grow-1',
          'px-4 pt-4 pb-3 px-md-5 py-md-4',
          customClass,
          styleclass
        )}
      >
        {/* Card image section (left side on desktop) */}
        <CobrandImage
          defaultImageURL={imagePath}
          title=""
          customClass="card-overview__image mr-lg-5 ml-lg-2"
          altText={altText}
        />

        {/* Card content section (right side on desktop) */}
        <div className={clsx('d-flex flex-column flex-grow-1', 'mt-2 mt-lg-0 mr-2')}>
          {/* Card header section */}
          {header && header.length > 0 && (
            <RichText componentId={`header-${componentId}`} text={header} customClass="card-overview-header" />
          )}

          {/* Points section separator */}
          {(pointSectionSeparator === true || pointSectionSeparator === StringBoolean.TRUE) && <hr className="mb-2" />}

          {/* Points section description */}
          {pointSectionDescription && pointSectionDescription.length > 0 && (
            <RichText
              componentId={`pointsSection-${componentId}`}
              text={pointSectionDescription}
              customClass="card-overview-subheader mt-2 mt-md-1 mb-2 mb-md-0"
            />
          )}

          {/* Card section separator */}
          {(cardSectionSeparator === true || cardSectionSeparator === StringBoolean.TRUE) && (
            <hr className="mt-0 mt-lg-1" />
          )}

          {/* Card section description */}
          {cardSectionDescription && cardSectionDescription.length > 0 && (
            <RichText
              componentId={`cardSection-${componentId}`}
              text={cardSectionDescription}
              customClass="card-overview-subheader card-overview-richtext d-flex flex-column flex-grow-1 mt-md-2 mt-lg-1"
            />
          )}

          {/* Call-to-action button */}
          <div>
            <Button
              id={`${componentId}-btn`}
              isLink
              href={linkUrl}
              ref={buttonRef}
              target={isOpenInNewTab ? LinkTarget.BLANK : LinkTarget.SELF}
              className="m-button-m mt-2 mt-md-3 mt-lg-2 mb-0 px-3"
              buttonCopy={linkText}
              linkType={linkType}
              linkAriaLabelOpenNewWindow={isOpenInNewTab ? ARIA_LABELS.OPEN_NEW_TAB : ''}
              trackingProperties={trackingProperties}
            />
          </div>
        </div>
      </div>
    </StyledCardOverviewHorizontal>
  );
});

CardOverviewHorizontal.displayName = 'CardOverviewHorizontal';
